import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { baseDataStatusLabels, formatInteger, unitCountsSummary, type BaseDataStatus } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Banner,
  Button,
  FilterChip,
  ImageSlot,
  Pill,
  Popover,
  SearchField,
  Segmented,
  Skeleton,
} from '../../../ds/components';
import { useIsCompact, useIsMobile } from '../../../ds/hooks';
import { errorMessage } from '../../../lib/api';
import { usePermissions } from '../../../lib/session';
import { ModuleEmptyPage } from '../../../shell/ModulePages';
import { useProperties } from '../api';
import { SalesBar, StatusPill } from '../bits';
import { ListSwitch } from './ListSwitch';
import { PICTURE_SIZES, pictureOf } from '../media/gallery';
import type { PropertyListItem, PropertyStatus } from '../types';
import styles from './PropertiesPage.module.css';

type View = 'tiles' | 'list';

/** The status pills of the „Filter" popover (Objekte 01), in the order of the sales process. */
const STATUS_FILTERS: { key: PropertyStatus; label: string }[] = [
  { key: 'acquisition', label: 'In Ankaufsprüfung' },
  { key: 'pre_sale', label: 'Vorvertrieb' },
  { key: 'on_sale', label: 'Im Vertrieb' },
  { key: 'sell_off', label: 'Abverkauf' },
  { key: 'archived', label: 'Archiviert' },
];

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Cover of the overview: the tile takes `thumb`/`medium` by its width, the table row's 36 px swatch `thumb` only. */
function heroPicture(item: PropertyListItem, upTo: 'thumb' | 'medium') {
  return item.heroFileId ? pictureOf(item.heroFileId, { upTo, fallback: upTo }) : null;
}

function Tile({ item }: { item: PropertyListItem }) {
  const tileHero = heroPicture(item, 'medium');
  return (
    <Link to={`/properties/${item.id}`} className={styles.tile} aria-label={item.name}>
      <div className={styles.tileImage}>
        <ImageSlot
          chrome="frame"
          src={tileHero?.src}
          srcSet={tileHero?.srcSet}
          sizes={PICTURE_SIZES.listTile}
          loading="lazy"
          alt={`Objektbild · ${item.name}`}
          placeholder={`Objektbild · ${item.name}`}
          height={188}
          radius="none"
        />
        <span className={styles.glassPill}>{item.statusLabel}</span>
      </div>
      <div className={styles.tileBody}>
        <div className={styles.tileTitle}>{item.name}</div>
        <div className={styles.tileAddress}>{item.addressLine ?? '—'}</div>
        <div className={styles.tileMetrics}>
          <div>
            <div className={styles.metricLabel}>Kaufpreise</div>
            <div className={styles.metricValue}>{item.priceRange?.label ?? '—'}</div>
          </div>
          <div className={styles.metricEnd}>
            <div className={styles.metricLabel}>Einheiten</div>
            <div className={styles.metricValue}>{formatInteger(item.units.total)}</div>
          </div>
        </div>
        <div className={styles.tileBar}>
          <SalesBar
            sold={item.units.sold}
            reserved={item.units.reserved}
            available={item.units.available}
            total={item.units.total}
            exclusive={item.units.exclusive}
            notReleased={item.units.notReleased}
          />
        </div>
      </div>
    </Link>
  );
}

function TileSkeleton() {
  return (
    <div className={clsx(styles.tile, styles.tileStatic)} aria-hidden="true">
      <Skeleton variant="block" height={188} className={styles.skeletonImage} />
      <div className={styles.tileBody}>
        <Skeleton width="60%" height={20} />
        <Skeleton width="80%" height={12} className={styles.skeletonLine} />
        <div className={styles.tileMetrics}>
          <Skeleton width={140} height={30} />
          <Skeleton width={40} height={30} />
        </div>
        <Skeleton height={6} className={styles.skeletonBar} />
      </div>
    </div>
  );
}

/** Tones of the „Stammdaten" Ampel (D-317). */
const BASE_DATA_TONES: Record<BaseDataStatus, 'success' | 'warning' | 'danger'> = {
  complete: 'success',
  partial: 'warning',
  empty: 'danger',
};

function ListRow({
  item,
  onOpen,
  showBaseData,
}: {
  item: PropertyListItem;
  onOpen: () => void;
  showBaseData: boolean;
}) {
  return (
    <div
      role="row"
      tabIndex={0}
      className={clsx(styles.row, showBaseData && styles.withBaseData)}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div role="cell" className={styles.rowName}>
        <span className={styles.rowThumb} aria-hidden="true">
          <ImageSlot
            chrome="frame"
            src={heroPicture(item, 'thumb')?.src}
            loading="lazy"
            alt=""
            placeholder=""
            height={36}
            radius="media"
          />
        </span>
        <span className={styles.rowTitle}>{item.name}</span>
      </div>
      <span role="cell" className={styles.rowAddress}>
        {item.addressLine ?? '—'}
      </span>
      <span role="cell" className={styles.rowNumber}>
        {formatInteger(item.units.total)}
      </span>
      <span role="cell" className={styles.rowNumber}>
        {item.priceRange?.label ?? '—'}
      </span>
      <div role="cell">
        <SalesBar
          sold={item.units.sold}
          reserved={item.units.reserved}
          available={item.units.available}
          total={item.units.total}
          exclusive={item.units.exclusive}
          notReleased={item.units.notReleased}
          height={5}
          legend="line"
        />
      </div>
      <span role="cell" className={styles.rowStatus}>
        <StatusPill tone={item.statusTone}>{item.statusLabel}</StatusPill>
      </span>
      <span role="cell" className={styles.rowDeveloper}>
        {item.developer?.name ?? '—'}
      </span>
      {showBaseData ? (
        <span role="cell" className={styles.rowBaseData}>
          <Pill
            variant={BASE_DATA_TONES[item.baseDataStatus]}
            title={`${item.baseDataDone} von ${item.baseDataTotal} Pflichtangaben`}
          >
            {baseDataStatusLabels[item.baseDataStatus]}
          </Pill>
        </span>
      ) : null}
    </div>
  );
}

/** Objekte 01 (Kacheln) and 02 (Liste): `/properties?view=tiles|list&status=&q=`. */
export function PropertiesPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const permissions = usePermissions();
  const mobile = useIsMobile();
  const compact = useIsCompact();
  const view: View = params.get('view') === 'list' ? 'list' : 'tiles';
  const statusParam = params.get('status') ?? '';
  const statuses = useMemo(
    () =>
      statusParam
        .split(',')
        .filter((entry): entry is PropertyStatus => STATUS_FILTERS.some((f) => f.key === entry)),
    [statusParam],
  );
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);
  const debouncedSearch = useDebounced(search.trim(), 350);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (debouncedSearch === q) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (debouncedSearch) next.set('q', debouncedSearch);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, q, setParams]);

  function patch(changes: Record<string, string | null>) {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      return next;
    });
  }

  function toggleStatus(key: PropertyStatus) {
    const next = statuses.includes(key) ? statuses.filter((entry) => entry !== key) : [...statuses, key];
    patch({ status: next.join(',') });
  }

  const list = useProperties(useMemo(() => ({ q, status: statuses }), [q, statuses]));
  const items = useMemo(() => list.data?.pages.flatMap((page) => page.items) ?? [], [list.data]);
  const header = list.data?.pages[0]?.header;
  const filtered = q !== '' || statuses.length > 0;
  const canCreate = permissions.is('properties.acquisition', 'yes');

  if (list.isSuccess && items.length === 0 && !filtered) return <ModuleEmptyPage module="properties" />;

  const summary = header ? unitCountsSummary(header.units) : null;
  const activeFilters = statuses.length + (q ? 1 : 0);

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <h1 className={styles.title}>Objekte</h1>
        <ListSwitch value="properties" />
        {header ? (
          <span className={styles.countPill}>{formatInteger(header.onSaleCount)} im Vertrieb</span>
        ) : (
          <Skeleton width={90} height={24} />
        )}
        {summary ? <span className={styles.summary}>{summary}</span> : <span className={styles.summary} />}
        {!mobile ? (
          <Segmented<View>
            label="Ansicht"
            className={styles.segmented}
            value={view}
            items={[
              { value: 'tiles', label: 'Kacheln' },
              { value: 'list', label: 'Liste' },
            ]}
            onChange={(value) => patch({ view: value === 'list' ? 'list' : null })}
          />
        ) : null}
        <button
          ref={filterRef}
          type="button"
          className={clsx('pressable', styles.filterButton, activeFilters > 0 && styles.filterButtonActive)}
          aria-haspopup="dialog"
          aria-expanded={filterOpen}
          onClick={() => setFilterOpen((open) => !open)}
        >
          Filter{activeFilters > 0 ? ` · ${activeFilters}` : ''}
        </button>
        {canCreate ? (
          <Button className={styles.createButton} onClick={() => void navigate('/properties/new')}>
            Objekt anlegen
          </Button>
        ) : null}
      </div>

      <Popover
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        anchorRef={filterRef}
        placement="bottom-end"
        width={360}
        variant="panel"
        label="Filter"
      >
        <div className={styles.filterPanel}>
          <SearchField
            label="Objekte durchsuchen"
            placeholder="Name, Ort oder Straße"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className={styles.filterChips} role="group" aria-label="Status">
            {STATUS_FILTERS.map((filter) => (
              <FilterChip
                key={filter.key}
                label={filter.label}
                active={statuses.includes(filter.key)}
                onClick={() => toggleStatus(filter.key)}
              />
            ))}
          </div>
          {activeFilters > 0 ? (
            <Button
              variant="ghost"
              size="small"
              onClick={() => {
                setSearch('');
                patch({ status: null, q: null });
              }}
            >
              Filter zurücksetzen
            </Button>
          ) : null}
        </div>
      </Popover>

      {list.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void list.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(list.error)}
        </Banner>
      ) : list.isPending ? (
        <div className={styles.grid} aria-busy="true" aria-label="Wird geladen">
          {Array.from({ length: mobile ? 2 : 6 }, (_, index) => (
            <TileSkeleton key={index} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className={styles.empty}>Keine Objekte gefunden.</p>
      ) : view === 'list' && !mobile ? (
        <div className={styles.table} role="table" aria-label="Objekte">
          <div role="row" className={clsx(styles.tableHead, !compact && styles.withBaseData)}>
            <span role="columnheader">Objekt</span>
            <span role="columnheader">Adresse</span>
            <span role="columnheader" className={styles.headEnd}>
              Einheiten
            </span>
            <span role="columnheader" className={styles.headEnd}>
              Kaufpreise
            </span>
            <span role="columnheader">Vertriebsstand</span>
            <span role="columnheader">Status</span>
            <span role="columnheader" className={styles.headEnd}>
              Bauträger
            </span>
            {!compact ? <span role="columnheader">Stammdaten</span> : null}
          </div>
          {items.map((item) => (
            <ListRow
              key={item.id}
              item={item}
              showBaseData={!compact}
              onOpen={() => void navigate(`/properties/${item.id}`)}
            />
          ))}
        </div>
      ) : (
        <div className={styles.grid}>
          {items.map((item) => (
            <Tile key={item.id} item={item} />
          ))}
        </div>
      )}

      {list.hasNextPage ? (
        <div className={styles.more}>
          <Button
            variant="secondary"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            Weitere laden
          </Button>
        </div>
      ) : null}
    </div>
  );
}
